import React, { useId, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import {
  CommonFieldProps,
  commonInputStyle,
  Field,
  FormErrorIcon,
  FormWarningIcon,
  getChildElementId,
  HelperTextWrapper,
  InputHintMessage,
  InputStyleProps,
  InputWrapper,
  LABEL_SELECTOR,
  ReadOnlyStyle,
  useTextAreaAutosize,
} from '@ardoq/forms';
import { colors } from '@ardoq/design-tokens';
import { header4 } from '@ardoq/typography';

type HeightProperty = 'auto' | '100%';

const TextAreaStyle = styled.textarea<
  InputStyleProps & { $height?: HeightProperty }
>`
  &::placeholder {
    color: ${colors.textSubtle};
  }
  background-color: ${colors.surfaceDefault};
  ${commonInputStyle};
  color: inherit;
  resize: none;
  ${({ $height }) => ($height ? `height: ${$height};` : '')}
`;

const FieldWrapper = styled(Field)`
  ${LABEL_SELECTOR} {
    ${header4};
  }

  ${HelperTextWrapper} {
    font-size: 12px;
  }
`;

type TextAreaWithCounterProps = Omit<
  CommonFieldProps,
  'hasWarning' | 'hasError'
> & {
  id?: string;
  autoFocus?: boolean;
  isReadOnly?: boolean;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  errorMessage?: string;
  hideWarningIcon?: boolean;
  characterLimit?: number;
  characterLimitWarning?: string;
  fixedHeight?: boolean;
  minRows?: number;
  onValueChange?: (value: string) => void;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
};
export const TextAreaWithCounter = React.forwardRef<
  HTMLTextAreaElement,
  TextAreaWithCounterProps
>(
  (
    {
      autoFocus,
      isDisabled,
      isReadOnly,
      indicator,
      warningMessage: warningMessageProp,
      errorMessage,
      helperText,
      label,
      hideWarningIcon,
      value,
      defaultValue,
      fixedHeight,
      minRows,
      formSize,
      onChange,
      onValueChange,
      onFocus,
      onBlur,
      popoverHelpContent,
      characterLimit,
      characterLimitWarning,
      dataTestId,
      ...props
    },
    forwardedRef
  ) => {
    const [characterCount, setCharacterCount] = useState(
      value?.length || defaultValue?.length || 0
    );
    const { ref } = useTextAreaAutosize(value);
    useImperativeHandle(forwardedRef, () => ref.current!);

    const generatedTextInputId = useId();
    const id = props.id ?? generatedTextInputId;

    const isOverCharacterLimit = Boolean(
      characterLimit && characterCount > characterLimit
    );
    const counterText =
      characterLimit && `${characterCount}/${characterLimit} characters`;
    const counterWarningMessage = isOverCharacterLimit
      ? `${counterText}${
          characterLimitWarning ? `. ${characterLimitWarning}` : ''
        }`
      : undefined;

    const warningMessage = warningMessageProp || counterWarningMessage;

    const hasError = Boolean(errorMessage);
    const hasWarning = !hasError && Boolean(warningMessage);

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.currentTarget.value;
      setCharacterCount(newValue.length);
      onValueChange?.(newValue);
      onChange?.(e);
    };

    const styleProps: InputStyleProps = {
      $isDisabled: isDisabled,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hideWarningIcon: hideWarningIcon,
    };
    return (
      <FieldWrapper
        label={label}
        inputId={id}
        helperText={helperText}
        isDisabled={isDisabled}
        indicator={indicator}
        errorMessage={errorMessage}
        warningMessage={warningMessage || counterWarningMessage}
        popoverHelpContent={popoverHelpContent}
        formSize={formSize}
      >
        <InputWrapper {...styleProps}>
          {isReadOnly ? (
            <ReadOnlyStyle>{value}</ReadOnlyStyle>
          ) : (
            <TextAreaStyle
              {...props}
              {...styleProps}
              onChange={onInputChange}
              onFocus={onFocus}
              onBlur={onBlur}
              value={value}
              defaultValue={defaultValue}
              ref={fixedHeight ? undefined : ref}
              $height={fixedHeight ? '100%' : 'auto'}
              disabled={isDisabled}
              rows={minRows || 1}
              data-testid={dataTestId}
              data-test-id={dataTestId}
              autoFocus={autoFocus}
              id={id}
              aria-describedby={
                helperText ? getChildElementId(id, 'help-text') : undefined
              }
            />
          )}
          {hasError && <FormErrorIcon />}
          {hasWarning && !hideWarningIcon && <FormWarningIcon />}
        </InputWrapper>
        {counterText && !hasWarning && !hasError && (
          <InputHintMessage>{counterText}</InputHintMessage>
        )}
      </FieldWrapper>
    );
  }
);
